// Display-side formatting helpers. Pure functions, no state.

// MySQL numeric type detection. tinyint(1) is excluded since it's commonly
// used as a boolean — leave it left-aligned, unformatted.
export function isNumericType(type) {
  if (!type) return false;
  const lower = String(type).toLowerCase();
  if (/^tinyint\s*\(\s*1\s*\)/.test(lower)) return false;
  return /^(tinyint|smallint|mediumint|int|integer|bigint|decimal|numeric|float|double|real)\b/.test(lower);
}

// In-cell editor text ⇄ value. The editor shows SQL NULL as the bare word NULL
// (typing NULL sets SQL NULL — the grid's long-standing convention). A STRING
// whose text is NULL — optionally already wrapped in single quotes — is shown
// with one more pair of quotes, so the two never collide and every value
// round-trips: the text 'NULL' edits as `'NULL'`, `''NULL''` is the text 'NULL'
// in quotes, and so on.
const QUOTED_NULL_RE = /^('*)NULL\1$/;
export function editTextOf(v) {
  if (v === null || v === undefined) return 'NULL';
  const s = String(v);
  return QUOTED_NULL_RE.test(s) ? `'${s}'` : s;
}
export function parseEditText(raw) {
  if (raw === 'NULL') return null;
  const m = /^'(('*)NULL\2)'$/.exec(raw);
  return m ? m[1] : raw;
}

// Integer-family column types (incl. BIT), whose values must be sent as exact
// integers in a row key.
export function isIntegerType(type) {
  return /^\s*(tinyint|smallint|mediumint|int|integer|bigint|bit)\b/i.test(String(type || ''));
}

// Binary column types — values that aren't valid UTF-8 can't travel exactly as
// JSON text (they arrive with U+FFFD in them), so such cells are read-only.
export function isBinaryType(type) {
  return /^\s*(binary|varbinary|tinyblob|blob|mediumblob|longblob)\b/i.test(String(type || ''));
}

// Classify a MySQL temporal type for display/edit: 'date' | 'datetime' | null.
// TIMESTAMP is treated as datetime; TIME / YEAR are left unformatted (null).
export function temporalKind(type) {
  if (!type) return null;
  const t = String(type).toLowerCase();
  if (/^(datetime|timestamp)\b/.test(t)) return 'datetime';
  if (/^date\b/.test(t)) return 'date';
  return null;
}

// Parse the allowed values out of a MySQL ENUM column type, e.g.
// `enum('Y','N')` → ['Y','N']. Handles embedded commas, '' escapes, and
// backslash escapes inside the quoted members. Returns null for any non-enum
// type (or a malformed one), so callers can `enumValues(type) || …`. SET types
// are intentionally NOT matched (multi-value; not a single-pick dropdown).
export function enumValues(type) {
  if (!type) return null;
  const s = String(type).trim();
  const m = /^enum\s*\(/i.exec(s);
  if (!m) return null;
  const body = s.slice(m[0].length);
  const out = [];
  let i = 0;
  while (i < body.length) {
    while (i < body.length && (body[i] === ' ' || body[i] === ',')) i++;
    if (i >= body.length || body[i] === ')') break;
    if (body[i] !== "'") return null; // malformed member list
    i++; // opening quote
    let val = '';
    let closed = false;
    while (i < body.length) {
      const c = body[i];
      if (c === '\\' && i + 1 < body.length) { val += body[i + 1]; i += 2; continue; }
      if (c === "'") {
        if (body[i + 1] === "'") { val += "'"; i += 2; continue; } // '' → '
        i++; closed = true; break;
      }
      val += c; i++;
    }
    if (!closed) return null; // unterminated member → malformed
    out.push(val);
  }
  return out.length ? out : null;
}

// Whether a column's information_schema EXTRA marks it as generated/computed
// (STORED/VIRTUAL/PERSISTENT) — such a column can't be inserted into. Mirrors
// the backend mysql.isGeneratedColumn, incl. NOT treating MySQL 8's
// "DEFAULT_GENERATED" (an expression default) as generated.
export function isGeneratedExtra(extra) {
  if (!extra) return false;
  const e = String(extra).toUpperCase().replace(/DEFAULT_GENERATED/g, '');
  return e.includes('GENERATED') || e.includes('VIRTUAL') || e.includes('PERSISTENT') || e.includes('STORED');
}

// Reformat a temporal value to 'YYYY-MM-DD' (date) or 'YYYY-MM-DD HH:mm:ss'
// (datetime/timestamp) by slicing the components out of whatever the driver
// returned — ISO ("…T…Z"), space-separated, or date-only. Done by string match,
// never `new Date()`, so the browser timezone can't shift the value.
// Unrecognized values pass through unchanged.
export function formatTemporal(v, kind) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}:\d{2}:\d{2}))?/);
  if (!m) return s;
  if (kind === 'date') return m[1];
  return m[2] ? `${m[1]} ${m[2]}` : m[1];
}

// Single-line preview of a long/multiline value: collapse whitespace runs to
// single spaces, trim, cap to `max` chars with an ellipsis.
export function collapsePreview(v, max = 200) {
  const s = String(v).replace(/\s+/g, ' ').trim();
  return s.length > max ? s.slice(0, max) + '…' : s;
}

// Human-readable byte size (B / KB / MB / GB). Shared by the export menu and the
// progress modal.
export function formatBytes(n) {
  n = n || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// Coarse relative-time label for a unix-seconds timestamp ("3h ago"). Shared
// by the query history/scraps sidebar and the Fleet Health last-backup column.
export function relTime(unixSec) {
  const diff = Date.now() / 1000 - unixSec;
  if (diff < 60) return 'just now';
  if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
  return Math.floor(diff / 86400) + 'd ago';
}

// Apply a Forms-DSL `format` mask to a value for display.
//   - A mask containing letters is a DATE pattern: yyyy / yy / MM / dd / HH / mm /
//     ss (e.g. "dd/MM/yyyy", "yyyy-MM-dd HH:mm"). The value is parsed by string
//     match (never `new Date()`, so the browser timezone can't shift it).
//   - Otherwise it's a NUMBER mask using # / 0 / , / . (e.g. "#,##0", "#,##0.00",
//     "0.00"): the digits after "." set the decimal places, a "," turns on
//     thousand grouping.
// A value that doesn't fit the mask (non-numeric for a number mask, unparseable
// for a date mask) passes through as text.
export function applyFormat(value, mask) {
  if (value === null || value === undefined || value === '') return value == null ? '' : String(value);
  if (!mask) return String(value);
  if (/[a-zA-Z]/.test(mask)) {
    const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}):(\d{2}))?/);
    if (!m) return String(value);
    const [, yyyy, MM, dd, HH = '00', mm = '00', ss = '00'] = m;
    return mask
      .replace(/yyyy/g, yyyy).replace(/yy/g, yyyy.slice(2))
      .replace(/MM/g, MM).replace(/dd/g, dd)
      .replace(/HH/g, HH).replace(/mm/g, mm).replace(/ss/g, ss);
  }
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  const dot = mask.indexOf('.');
  const decimals = dot >= 0 ? mask.length - dot - 1 : 0;
  let s = num.toFixed(decimals);
  if (mask.includes(',')) {
    const neg = s.startsWith('-'); if (neg) s = s.slice(1);
    const [ip, dp] = s.split('.');
    const g = ip.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    s = (neg ? '-' : '') + (dp !== undefined ? `${g}.${dp}` : g);
  }
  return s;
}

// Format a numeric value with thousand separators, preserving decimal places
// exactly as the source string (so trailing zeros and precision are kept).
// Non-numeric input is returned as-is.
export function formatNumberWithSep(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (!/^-?\d+(\.\d+)?$/.test(s)) return s;
  const [intPart, decPart] = s.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decPart !== undefined ? `${grouped}.${decPart}` : grouped;
}

// formatDuration renders whole seconds as "8s" / "3m 7s" / "1h 2m" — the
// compact elapsed-time style shared by the process monitor and AI Actions.
export function formatDuration(secs) {
  const s = Math.max(0, Math.floor(Number(secs) || 0));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}
